'use client';

import { AttributeSide } from '@eliadmin/core/src/components/categories/attributes/attribute';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useCategoryFilterContext } from './lazy-category-provider';
import { CategoryFilterContextPlaceholder } from './category-filter-context-placeholder';
import { ProgressiveCategoryAttributes } from './progressive-category-attributes';

const FilterSeparator = () => <Separator className="w-full bg-border last-of-type:hidden" />;

export function CategoryFilterLeft() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();
  const clearFilter = useCategoryStore(s => s.clearFilters);
  const { status } = useCategoryFilterContext();

  const attributes = category.attributes?.filter(
    attribute => attribute.values?.length && attribute.name,
  );

  if (status !== 'ready') {
    return (
      <Card
        className={cn(
          'w-full rounded-[12px] border border-border p-4 lg:p-6 xl:p-8',
          'ea-category-filter-left',
        )}
      >
        <CategoryFilterContextPlaceholder />
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'flex w-full flex-col gap-8 rounded-[12px] border border-border p-4 lg:p-6 xl:p-8',
        'ea-category-filter-left',
      )}
    >
      <div className={cn('ea-category-filter-left__content-wrapper')}>
        <div className={cn('flex flex-col gap-4', 'ea-category-filter-left__content')}>
          {/* subcategories */}
          {!!templateSettings.category?.categories_in_filter?.enabled &&
            !!category.sub_categories?.length && (
              <>
                <AttributeSide
                  name={t('text337')}
                  type="subCategory"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category?.categories_in_filter?.order}
                  className={{ wrapper: cn('', 'ea-category-filter-left__subcategories') }}
                />
                <FilterSeparator />
              </>
            )}

          {/* manufacturers */}
          {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
            !!category.manufacturers?.length && (
              <>
                <AttributeSide
                  name={t('text195')}
                  type="generic"
                  attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                  order={templateSettings.category?.manufacturers_in_filter?.order}
                  className={{ wrapper: cn('', 'ea-category-filter-left__manufacturers') }}
                />
                <FilterSeparator />
              </>
            )}

          {/* statuses */}
          {!!templateSettings.category?.statuses_in_filter?.enabled &&
            !!category.statuses?.length && (
              <>
                <AttributeSide
                  name={t('text518')}
                  type="generic"
                  attribute={{ type: 'status', type2: 'statuses' }}
                  order={templateSettings.category?.categories_in_filter?.order}
                  className={{ wrapper: cn('', 'ea-category-filter-left__statuses') }}
                />
                <FilterSeparator />
              </>
            )}

          {/* availabilities */}
          {!!templateSettings.category?.availability_in_filter?.enabled &&
            !!category.availabilities?.length && (
              <>
                <AttributeSide
                  name={t('text339')}
                  type="generic"
                  attribute={{ type: 'availability', type2: 'availabilities' }}
                  order={templateSettings.category.availability_in_filter.order}
                  className={{ wrapper: cn('', 'ea-category-filter-left__availabilities') }}
                />
                <FilterSeparator />
              </>
            )}

          {/* attributes */}
          {!!templateSettings.category?.attributes_in_filter?.enabled &&
            !!category.attributes?.length && (
              // <div className={`order-${templateSettings.category.attributes_in_filter.order}`}>
              <>
                <ProgressiveCategoryAttributes
                  attributes={attributes ?? []}
                  className={cn('', 'ea-category-filter-left__attributes')}
                  separatorClassName="w-full bg-border last-of-type:hidden"
                />
              </>
            )}

          {/* price */}
          {!!templateSettings.category?.price_in_filter?.enabled && category.price.max !== null && (
            <>
              <AttributeSide
                name={t('text340')}
                type="price"
                attribute={{ type: 'status', type2: 'statuses' }}
                order={templateSettings.category.price_in_filter.order}
                className={{ wrapper: cn('', 'ea-category-filter-left__price') }}
              />
              <FilterSeparator />
            </>
          )}

          {!!templateSettings.category?.suppliers_in_filter?.enabled &&
            !!category.suppliers?.length && (
              <>
                <AttributeSide
                  name={t('text718')}
                  type="generic"
                  attribute={{ type: 'supplier', type2: 'suppliers' }}
                  className={{ wrapper: cn('', 'ea-category-filter-left__suppliers') }}
                  order={templateSettings.category.suppliers_in_filter.order}
                />
                <FilterSeparator />
              </>
            )}
          {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
            <>
              <AttributeSide
                name={t('text728')}
                type="supplierLocation"
                attribute={{ type: 'supplier', type2: 'suppliers' }}
                className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                order={templateSettings.category?.suppliers_in_filter?.order}
              />
              <FilterSeparator />
            </>
          )}
        </div>
      </div>
      <Button
        variant={'none'}
        size={'none'}
        className="mx-auto text-[#666666]"
        onClick={clearFilter}
      >
        {t('text3384')}
      </Button>
    </Card>
  );
}
